<template>
  <AdminLayout>
    <template #content>
      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Fiche fournisseur
            <span v-if="supplier" class="text-muted">#{{ supplier.name }}</span>
          </h1>
          <div v-if="supplier" class="small text-muted">
            Créé le {{ $format.datetime(supplier.created_at) }}
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'Suppliers' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!supplier"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
        </div>
      </div>

      <!-- ALERTS -->
      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <!-- LOADER -->
      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du fournisseur...</div>
      </div>

      <!-- NON TROUVÉ -->
      <div v-else-if="!supplier && !errorMessage" class="alert alert-warning">
        Fournisseur introuvable.
      </div>

      <!-- CONTENU PRINCIPAL -->
      <div v-else-if="supplier" class="row g-3 align-items-start">
        <!-- INFOS FOURNISSEUR -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div
              class="card-header bg-white border-0 pb-1 d-flex justify-content-between align-items-center"
            >
              <h6 class="mb-0">Informations fournisseur</h6>
              <span
                class="badge"
                :class="supplier.is_active ? 'bg-success' : 'bg-secondary'"
              >
                {{ supplier.is_active ? 'Actif' : 'Inactif' }}
              </span>
            </div>

            <div class="card-body pt-2 pb-3">
              <div class="mb-2">
                <div class="small text-muted">Nom</div>
                <div class="fw-semibold">{{ supplier.name }}</div>
              </div>

              <div v-if="supplier.contact_name" class="mb-2">
                <div class="small text-muted">Contact principal</div>
                <div>{{ supplier.contact_name }}</div>
              </div>

              <div v-if="supplier.phone" class="mb-2">
                <div class="small text-muted">Téléphone</div>
                <div>{{ supplier.phone }}</div>
              </div>

              <div v-if="supplier.email" class="mb-2">
                <div class="small text-muted">Email</div>
                <div>{{ supplier.email }}</div>
              </div>

              <div v-if="supplier.city || supplier.address" class="mb-2">
                <div class="small text-muted">Adresse</div>
                <div>
                  <span v-if="supplier.address">{{ supplier.address }}</span>
                  <span v-if="supplier.city">, {{ supplier.city }}</span>
                </div>
              </div>

              <div v-if="supplier.tax_number" class="mb-2">
                <div class="small text-muted">N° fiscal / RCCM</div>
                <div>{{ supplier.tax_number }}</div>
              </div>

              <div v-if="supplier.notes" class="mt-2">
                <div class="small text-muted">Notes internes</div>
                <div>{{ supplier.notes }}</div>
              </div>

              <hr />

              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Achats validés</span>
                <span class="fw-semibold">{{ supplier.summary?.validated_purchases_count || 0 }}</span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Montant total achats</span>
                <span class="fw-bold">
                  {{ $format.currency(supplier.summary?.total_purchases_amount || 0) }}
                </span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Montant réglé</span>
                <span class="fw-semibold">
                  {{ $format.currency(supplier.summary?.paid_amount || 0) }}
                </span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Dette restante</span>
                <span class="fw-bold text-danger">
                  {{ $format.currency(supplier.summary?.debt_amount || 0) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- HISTORIQUE ACHATS -->
        <div class="col-lg-8">

            <div
              class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2"
            >
              <h6 class="mb-0">Historique des achats validés</h6>
              <small class="text-muted" v-if="purchasePaginationTotal">
                {{ purchasePaginationTotal }} achat(s)
              </small>
            </div>


              <BootstrapDataTable
                :response="purchasesResponse"
                :columns="purchaseColumns"
                row-key="id"
                :loading="isLoadingPurchases"
                :sort-by="purchaseTable.sortBy"
                :sort-dir="purchaseTable.sortDir"
                :per-page="purchaseTable.perPage"
                empty-text="Aucun achat enregistré pour ce fournisseur."
                @row-click="openPurchaseDetail"
                @page-change="onPurchasePageChange"
                @per-page-change="onPurchasePerPageChange"
                @sort-change="onPurchaseSortChange"
                @refresh="fetchPurchases"
              >
                <template #cell-status="{ row }">
                  <span class="badge" :class="purchaseStatusClass(row.status)">
                    {{ purchaseStatusLabel(row.status) }}
                  </span>
                </template>
                <template #cell-purchase_date="{ row }">
                  {{ $format.date(row.purchase_date) }}
                </template>
                <template #cell-total_amount="{ row }">
                  {{ $format.currency(row.total_amount || 0) }}
                </template>
                <template #cell-paid_amount="{ row }">
                  {{ $format.currency(row.paid_amount || 0) }}
                </template>
                <template #cell-due_amount="{ row }">
                  <span class="fw-semibold" :class="Number(row.due_amount || 0) > 0 ? 'text-danger' : ''">
                    {{ $format.currency(row.due_amount || 0) }}
                  </span>
                </template>
                <template #cell-payment_status="{ row }">
                  <span class="badge" :class="paymentStatusClass(row.payment_status)">
                    {{ paymentStatusLabel(row.payment_status) }}
                  </span>
                </template>
              </BootstrapDataTable>


        </div>

      </div>

      <!-- DIALOG ÉDITION -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '600px' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Modifier le fournisseur</h5>
        </template>

        <SupplierForm
          v-if="supplier"
          :mode="'edit'"
          :supplier="supplier"
          :submitting="isSavingForm"
          @save="handleSave"
          @cancel="closeFormDialog"
        />
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="purchaseDetailDialogVisible"
        :modal="true"
        :style="{ width: '900px', maxWidth: '100%' }"
        header="Détail de l’achat"
      >
        <div v-if="isLoadingPurchaseDetail" class="text-center py-4">
          <i class="fa fa-spinner fa-spin me-1"></i>
          Chargement...
        </div>

        <div v-else-if="selectedPurchase">
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <div class="small text-muted">Référence</div>
              <div class="fw-semibold">{{ selectedPurchase.reference }}</div>
              <div class="small text-muted mt-2">Date d'achat</div>
              <div>{{ $format.date(selectedPurchase.purchase_date) }}</div>
            </div>
            <div class="col-md-6 text-md-end">
              <div>
                <span class="badge" :class="purchaseStatusClass(selectedPurchase.status)">
                  {{ purchaseStatusLabel(selectedPurchase.status) }}
                </span>
                <span class="badge ms-1" :class="paymentStatusClass(selectedPurchase.payment_status)">
                  {{ paymentStatusLabel(selectedPurchase.payment_status) }}
                </span>
              </div>
              <div class="mt-2">
                <strong>{{ $format.currency(selectedPurchase.total_amount || 0) }}</strong>
                <div class="small text-muted">
                  Payé {{ $format.currency(selectedPurchase.paid_amount || 0) }} ·
                  Reste {{ $format.currency(selectedPurchase.due_amount || 0) }}
                </div>
              </div>
            </div>
          </div>

          <h6>Lignes d'achat</h6>
          <div class="table-responsive mb-3">
            <table class="table table-sm table-striped table-bordered align-middle">
              <thead class="table-light">
                <tr>
                  <th>Produit</th>
                  <th class="text-end">Qté</th>
                  <th class="text-end">PU</th>
                  <th>Lot</th>
                  <th>Expiration</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedPurchase.items || []" :key="item.id">
                  <td>{{ item.product_name }}</td>
                  <td class="text-end">{{ item.quantity }}</td>
                  <td class="text-end">{{ $format.currency(item.unit_price || 0) }}</td>
                  <td>{{ item.lot_number || '-' }}</td>
                  <td>{{ item.expiry_date ? $format.date(item.expiry_date) : '-' }}</td>
                  <td class="text-end fw-semibold">{{ $format.currency(item.line_total || 0) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6>Historique des règlements</h6>
          <div class="table-responsive">
            <table class="table table-sm table-striped table-hover table-bordered align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Date</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>Référence</th>
                  <th>Agent</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!selectedPurchase.payments || !selectedPurchase.payments.length">
                  <td colspan="5" class="text-center text-muted py-3">Aucun règlement.</td>
                </tr>
                <tr v-for="payment in selectedPurchase.payments || []" :key="payment.id">
                  <td>{{ $format.date(payment.payment_date) }}</td>
                  <td>{{ $format.currency(payment.amount || 0) }}</td>
                  <td>{{ payment.method || '-' }}</td>
                  <td>{{ payment.reference || '-' }}</td>
                  <td>{{ payment.user?.name || payment.user?.username || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import SupplierForm from './SupplierForm.vue';

export default {
  name: 'SupplierShowView',
  components: {
    SupplierForm,
  },
  data() {
    return {
      supplier: null,
      purchasesResponse: null,

      isLoading: false,
      isLoadingPurchases: false,
      isLoadingPurchaseDetail: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      isSavingForm: false,
      purchaseDetailDialogVisible: false,
      selectedPurchase: null,
      purchaseTable: {
        page: 1,
        perPage: 10,
        sortBy: 'purchase_date',
        sortDir: 'desc',
      },
      purchaseColumns: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'purchase_date', label: 'Date', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'total_amount', label: 'Total', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'paid_amount', label: 'Payé', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'due_amount', label: 'Dette', sortable: true, class: 'text-end', headerClass: 'text-end' },
        { key: 'payment_status', label: 'Règlement', sortable: true },
      ],
    };
  },
  computed: {
    purchasePaginationTotal() {
      return this.purchasesResponse?.meta?.pagination?.total || 0;
    },
  },
  mounted() {
    this.fetchSupplier();
  },
  methods: {
    async fetchSupplier() {
      const id = this.$route.params.id;

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.supplier = null;
      this.purchasesResponse = null;

      try {
        const res = await this.$api.get(`/suppliers/${id}`);
        const data = res.data.data || res.data;

        this.supplier = {
          id: data.id,
          name: data.name,
          contact_name: data.contact_name,
          phone: data.phone,
          email: data.email,
          city: data.city,
          address: data.address,
          tax_number: data.tax_number,
          is_active: data.is_active,
          notes: data.notes,
          created_at: data.created_at,
          summary: data.summary || {},
        };

        await this.fetchPurchases();
      } catch (error) {
        console.error('Erreur chargement fournisseur', error);
        this.errorMessage = 'Impossible de charger ce fournisseur.';
      } finally {
        this.isLoading = false;
      }
    },

    purchaseStatusLabel(status) {
      if (status === 'validated') return 'Validé';
      if (status === 'draft') return 'Brouillon';
      return status;
    },

    paymentStatusLabel(status) {
      if (status === 'paid') return 'Payé';
      if (status === 'partial') return 'Partiel';
      return 'Non payé';
    },

    paymentStatusClass(status) {
      if (status === 'paid') return 'bg-success';
      if (status === 'partial') return 'bg-warning text-dark';
      return 'bg-danger';
    },

    purchaseStatusClass(status) {
      if (status === 'validated') return 'bg-success';
      if (status === 'draft') return 'bg-secondary';
      return 'bg-light text-dark';
    },

    async fetchPurchases() {
      if (!this.supplier?.id) return;

      this.isLoadingPurchases = true;

      try {
        const res = await this.$api.get('/purchases', {
          params: {
            supplier_id: this.supplier.id,
            status: 'validated',
            paginate: 1,
            per_page: this.purchaseTable.perPage,
            page: this.purchaseTable.page,
            sort_by: this.purchaseTable.sortBy,
            sort_dir: this.purchaseTable.sortDir,
          },
        });
        this.purchasesResponse = res.data;
      } catch (error) {
        console.error('Erreur chargement achats fournisseur', error);
        this.errorMessage = 'Impossible de charger les achats du fournisseur.';
      } finally {
        this.isLoadingPurchases = false;
      }
    },

    onPurchasePageChange(page) {
      this.purchaseTable.page = page;
      this.fetchPurchases();
    },

    onPurchasePerPageChange(perPage) {
      this.purchaseTable.perPage = perPage;
      this.purchaseTable.page = 1;
      this.fetchPurchases();
    },

    onPurchaseSortChange({ sortBy, sortDir }) {
      this.purchaseTable.sortBy = sortBy;
      this.purchaseTable.sortDir = sortDir;
      this.purchaseTable.page = 1;
      this.fetchPurchases();
    },

    async openPurchaseDetail(purchase) {
      if (!purchase?.id) return;
      this.purchaseDetailDialogVisible = true;
      this.selectedPurchase = null;
      this.isLoadingPurchaseDetail = true;

      try {
        const res = await this.$api.get(`/purchases/${purchase.id}`);
        this.selectedPurchase = res.data.data;
      } catch (error) {
        console.error('Erreur détail achat fournisseur', error);
        this.errorMessage = "Impossible de charger le détail de l'achat.";
        this.purchaseDetailDialogVisible = false;
      } finally {
        this.isLoadingPurchaseDetail = false;
      }
    },

    openEditDialog() {
      if (!this.supplier) return;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    async handleSave(payload) {
      if (!this.supplier) return;
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        await this.$api.put(`/suppliers/${this.supplier.id}`, payload);
        this.successMessage = 'Fournisseur mis à jour avec succès.';
        this.formDialogVisible = false;
        await this.fetchSupplier();
      } catch (error) {
        console.error('Erreur mise à jour fournisseur', error);
        let msg =
          'Une erreur est survenue lors de la mise à jour du fournisseur.';

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          msg = Object.values(error.response.data.errors)
            .flat()
            .join(' ');
        }

        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}
</style>
